import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  CheckCircle2, 
  Clock, 
  Users, 
  Calendar,
  Settings,
  Loader2,
  ArrowRight,
  HeartHandshake,
  Heart,
  Check,
  Mail,
  ShieldCheck,
  Shield
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useLifely } from '../context/LifelyContext';
import { continuityAPI } from '../services/api';
import { toast } from 'sonner';
import EmergencyAccess from '../components/EmergencyAccess';

const ContinuityOverview = () => {
  const navigate = useNavigate();
  const { 
    continuityConfig, 
    fetchContinuity, 
    fetchSharing,
    shareSettings,
    performCheckIn,
    getContactById,
    isAuthenticated, 
    loading 
  } = useLifely();

  const [justCheckedIn, setJustCheckedIn] = React.useState(false);
  const [dataLoaded, setDataLoaded] = React.useState(false);
  const [resending, setResending] = React.useState(false);

  // Fetch data when component mounts
  useEffect(() => {
    const loadData = async () => {
      if (isAuthenticated && !dataLoaded) {
        try {
          await Promise.all([fetchContinuity(), fetchSharing()]);
          setDataLoaded(true);
        } catch (err) {
          console.error('Failed to load continuity data:', err);
        }
      }
    };
    loadData();
  }, [isAuthenticated, dataLoaded, fetchContinuity, fetchSharing]);

  // Handle null config gracefully
  const config = continuityConfig || {};
  const isConfigured = config.isConfigured || false;
  const checkInInterval = config.checkInInterval || config.checkInFrequency || 0;
  const checkInUnit = config.checkInUnit || 'days';
  const lastCheckIn = config.lastCheckIn;
  const nextCheckIn = config.nextCheckIn;

  // Check-in frequency in plain English ("Every month" / "Every 3 months")
  const unitSingular = { days: 'day', weeks: 'week', months: 'month' };
  const frequencyLabel = checkInInterval === 1
    ? `Every ${unitSingular[checkInUnit] || checkInUnit.replace(/s$/, '')}`
    : `Every ${checkInInterval} ${checkInUnit}`;

  // Notification-email verification state
  const emailVerified = config.email_verified === 1;
  const altEmail = config.alternativeEmail;

  const handleResendVerification = async () => {
    try {
      setResending(true);
      await continuityAPI.sendVerification({ source: 'settings' });
      toast.success('Verification email sent! Check your inbox.');
    } catch {
      toast.error('Failed to send verification email');
    } finally {
      setResending(false);
    }
  };

  // Get nominees from share settings
  const nominees = shareSettings?.filter(
    s => s.shareTrigger === 'continuity' || s.shareTrigger === 'both'
  ) || [];

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const handleCheckIn = async () => {
    try {
      const result = await performCheckIn();
      setJustCheckedIn(true);
      if (result?.nextCheckIn) {
        toast.success(`Checked in! Next check-in: ${formatDate(result.nextCheckIn)}`);
      } else {
        toast.success('Checked in successfully!');
      }
      setTimeout(() => setJustCheckedIn(false), 3000);
    } catch (err) {
      toast.error('Check-in failed. Please try again.');
    }
  };

  // Show loading state
  if ((loading.continuity || loading.sharing) && !dataLoaded) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in" data-testid="continuity-overview-page">
      {/* Header */}
      <section className="space-y-2">
        <h1 className="text-3xl sm:text-4xl font-heading font-semibold text-foreground">
          Continuity
        </h1>
        <p className="text-muted-foreground text-base max-w-2xl">
          A simple system to check in periodically. If you stop responding, your chosen people will be notified with the information you've configured.
        </p>
      </section>

      {/* Not Configured State */}
      {!isConfigured && (
        <Card className="border border-border bg-primary/5">
          <CardContent className="p-8">
            <div className="max-w-md mx-auto text-center">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <HeartHandshake className="w-6 h-6 text-primary" />
              </div>
              <h2 className="text-xl font-heading font-semibold mb-2">Set up your Continuity Heartbeat</h2>
              <p className="text-muted-foreground mb-6">
                A simple check-in system. If you stop responding, your chosen people are notified, nothing more.
              </p>
            </div>
            <div className="max-w-md mx-auto space-y-3 mb-6 text-left">
              {[
                { icon: Heart, text: 'Check in at your own pace, weekly, monthly, or quarterly' },
                { icon: Shield, text: 'Your data stays private until you choose to share it' },
                { icon: Users, text: 'Your nominees are only notified if you stop responding' },
              ].map(({ icon: Icon, text }, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                    <Icon className="w-4 h-4 text-primary" />
                  </div>
                  <p className="text-sm text-muted-foreground pt-1.5">{text}</p>
                </div>
              ))}
            </div>
            <div className="text-center">
              <Button size="lg" onClick={() => navigate('/continuity/setup')}>
                Get Started
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Configured State */}
      {isConfigured && (
        <>
          {/* Status Cards */}
          <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="continuity-status">
            {/* Status */}
            <Card className="border border-border bg-primary/5">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <CheckCircle2 className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Status</p>
                    <p className="font-medium text-primary">Active</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Frequency */}
            <Card className="border border-border">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-muted-foreground" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Check-in</p>
                    <p className="font-medium">{frequencyLabel}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Next Check-in */}
            <Card className="border border-border">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                    <Clock className="w-5 h-5 text-muted-foreground" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Next Check-in</p>
                    <p className="font-medium">{formatDate(nextCheckIn)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Nominees */}
            <Card className="border border-border">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                    <Users className="w-5 h-5 text-muted-foreground" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Nominees</p>
                    <p className="font-medium">{nominees.length} {nominees.length === 1 ? 'person' : 'people'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </section>

          {/* Notification email verification status */}
          {altEmail && !emailVerified && (
            <Card className="border border-amber-200 border-l-4 border-l-amber-400 bg-amber-50">
              <CardContent className="p-4 flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-amber-600 shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-amber-900">Notification email not verified</p>
                    <p className="text-xs text-amber-700">Check your inbox to verify {altEmail}</p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleResendVerification}
                  disabled={resending}
                  className="border-amber-300 text-amber-800 hover:bg-amber-100 hover:text-amber-900 shrink-0"
                >
                  {resending ? 'Sending…' : 'Resend'}
                </Button>
              </CardContent>
            </Card>
          )}
          {altEmail && emailVerified && (
            <Card className="border border-green-200 border-l-4 border-l-green-500 bg-green-50">
              <CardContent className="p-3 flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-green-600 shrink-0" />
                <p className="text-sm text-green-800">Notification email verified</p>
              </CardContent>
            </Card>
          )}

          {/* Check-in Action */}
          <Card className="border border-primary/20 border-l-4 border-l-primary bg-primary/5">
            <CardContent className="p-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center shrink-0 mt-0.5">
                    <HeartHandshake className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-medium">Confirm you're still here</h3>
                    <p className="text-sm text-muted-foreground mt-0.5">
                      {lastCheckIn 
                        ? `Last confirmed: ${formatDate(lastCheckIn)}`
                        : 'You haven\'t confirmed yet'
                      }
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Clicking this resets your check-in timer to {checkInInterval} {checkInUnit} from now and restarts the reminder count.
                    </p>
                  </div>
                </div>
                <Button
                  onClick={handleCheckIn}
                  disabled={justCheckedIn}
                  size="lg"
                  className="min-w-[160px] shrink-0"
                  data-testid="check-in-btn"
                >
                  {justCheckedIn ? (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      Confirmed!
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      I'm Still Here
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Nominees List */}
          <Card className="border border-border">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center justify-between text-base font-medium">
                <span className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-primary" />
                  Your Nominees
                </span>
                <Button variant="ghost" size="sm" asChild>
                  <Link to="/sharing">Manage</Link>
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {nominees.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No nominees configured.{' '}
                  <Link to="/sharing" className="text-primary hover:underline">Add some</Link>
                </p>
              ) : (
                <div className="space-y-2">
                  {nominees.map((share) => {
                    const contact = getContactById(share.contactId);
                    return (
                      <div 
                        key={share.id} 
                        className="flex items-center justify-between p-3 rounded-lg bg-muted/50"
                      >
                        <div>
                          <p className="font-medium text-sm">{share.contactName || contact?.name || 'Unknown'}</p>
                          <p className="text-xs text-muted-foreground">
                            {share.shareType === 'all_maps' 
                              ? 'Will receive all maps' 
                              : `Will receive ${share.selectedMaps?.length || 0} maps`
                            }
                            {share.includeWillStatus && ' + formal arrangements'}
                          </p>
                        </div>
                        <Badge variant="outline" className="text-xs">
                          {contact?.email ? 'Email' : 'No email'}
                        </Badge>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Button variant="outline" className="flex-1" asChild>
              <Link to="/continuity/settings">
                <Settings className="w-4 h-4 mr-2" />
                Edit Settings
              </Link>
            </Button>
            <Button variant="outline" className="flex-1" asChild>
              <Link to="/sharing">
                <Users className="w-4 h-4 mr-2" />
                Manage Nominees
              </Link>
            </Button>
          </div>
        </>
      )}

      {/* Emergency Access Section - only show when continuity is configured */}
      {isConfigured && (
        <EmergencyAccess />
      )}
    </div>
  );
};

export default ContinuityOverview;
